import { api, ResponseMap } from '@/lib/utils/api.utils';
import { ERROR_CODE } from '@/lib/config/errorCode';
import { SERVER_CODE } from '@/lib/config/serverCode';
import { storeSession } from '../hooks/useSession';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { CHAIN_IDS } from '../config/chain';
import { useSession } from '@/lib/hooks/useSession';
import { toaster } from '@/components/ui/toaster';

const requestUrl = process.env.NEXT_PUBLIC_REQUEST_URL;

export async function requestGetEmailCode(email: string): Promise<boolean> {
  try {
    const response = await api.post(`${requestUrl}/v1/account/get_email_code`, {
      json: {
        email_type: 0,
        email,
      },
    });
    return true;
  } catch (error) {
    console.error('Failed to get email code:', error);
    return false;
  }

  // if (!response.ok) {
  //   throw new Error(ERROR_CODE.NETWORK_ERROR);
  // }

  // const data = await response.json<ResponseMap<void>>();
}

export async function requestGetEmailCodeWithType(
  type: number,
): Promise<boolean> {
  try {
    const response = await api.post(`${requestUrl}/v1/account/get_email_code`, {
      json: {
        email_type: type,
      },
    });
    return true;
  } catch (error) {
    console.error('Failed to get email code:', error);
    return false;
  }

  // if (!response.ok) {
  //   throw new Error(ERROR_CODE.NETWORK_ERROR);
  // }

  // const data = await response.json<ResponseMap<void>>();
}

export async function requestEmailRegister(
  email: string,
  password: string,
  emailCode: string,
  inviteCode?: string,
): Promise<void> {
  const response = await api.post(`${requestUrl}/v1/account/email_register`, {
    json: {
      email,
      password,
      email_code: emailCode,
      invite_code: inviteCode,
    },
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<{ accessToken: string }>>();

  if (!data.data?.accessToken) {
    throw new Error(data.message);
  }
  storeSession({ jwt: data.data?.accessToken ?? '' });
}

export async function requestLogin(
  email: string,
  password: string,
  googleAuth: string,
): Promise<void> {
  const response = await api.post(`${requestUrl}/v1/account/email_login`, {
    json: {
      email,
      password,
      google_code: googleAuth,
    },
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<{ accessToken: string }>>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  storeSession({ jwt: data.data?.accessToken ?? '' });
}

export interface UserCurrencyListData {
  allWalletGroupId: string[];
  gasType: number;
  gasTypeBase: number;
  gasTypeBsc: number;
  gasTypeSol: number;
  gasTypeTrx: number;
  isAntiMev: number;
  isFastTrade: number;
  slippage: string;
  totalChainGasType: TotalChainGasType[];
  totalValue: number;
  userWalletGroups: UserWalletGroup[];
  usingWalletGroupId: string[];
}

export interface TotalChainGasType {
  chainId: string;
  gasType: number;
  gasTypePromptInfo: string;
}

export interface UserWalletGroup {
  isUsing?: number;
  userWallets?: UserWallet[];
  walletGroupId?: number;
  wallGroupValues?: number;
}

export interface UserWallet {
  chainId?: number;
  isUsing?: number;
  tokenValue?: number;
  transferInRemark?: string;
  txAmount?: string;
  userId?: number;
  userWalletAddress?: string;
  walletGroupId?: number;
  walletId?: number;
  walletTag?: string;
}

export async function requestUserCurrencyList(): Promise<UserCurrencyListData> {
  const response = await api.get(`${requestUrl}/v1/account/user_currency_list`);
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<UserCurrencyListData>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export enum WalletStatus {
  isUsing = 1,
  isNotUsing = 0,
}

export function useUserCurrencyList(
  chainId: number,
): UseQueryResult<UserCurrencyListData> {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['user_currency_list'],
    queryFn: requestUserCurrencyList,
    enabled: !!session?.isLogin,
  });
}

export interface UserTokenBalanceRequest {
  /**
   * chain_id
   */
  chain_id?: number;
  is_native?: boolean;
  /**
   * token address
   */
  token_address?: string;
  /**
   * 钱包id
   */
  wallet_group_id?: number;
}

export interface UserTokenBalanceData {
  balance: string;
  tokenDecimals: string;
}

export function useUserTokenBalance(
  payload: UserTokenBalanceRequest,
): UseQueryResult<string> {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['get_user_token_balance', payload],
    queryFn: () => requestUserTokenBalance(payload),
    select: (data) => data.balance,
    refetchInterval: 1000 * 60 * 3,
    enabled: !!session?.isLogin && payload.wallet_group_id !== undefined,
  });
}

export async function requestUserTokenBalance(
  payload: UserTokenBalanceRequest,
): Promise<UserTokenBalanceData> {
  const response = await api.get(
    `${requestUrl}/v1/account/get_user_token_balance`,
    {
      searchParams: { ...payload },
    },
  );
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<UserTokenBalanceData>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export type UserInfo = {
  diamond: string;
  email: string;
  isBind2FA: number;
  isOpenMonitorOffice: number;
  isOpenMonitorOfficeTradeAction: number;
  isOpenMonitorSelf: number;
  jpushRegisterId: string[];
  myInviteCode: string;
  myInviteUrl: string;
  totalChainGasType: string[];
  userId: string;
  userLoginAddress: string;
  userName: string;
  userWalletsInfo: {
    allWalletGroupId: string[];
    gasType: number;
    gasTypeBase: number;
    gasTypeBsc: number;
    gasTypeSol: number;
    gasTypeTrx: number;
    isFastTrade: number;
    slippage: number;
    isAutoSlippage: boolean;
    slippageDisplay: string;
    isAntiMev: boolean;
    totalChainGasType: string[];
    totalValue: number;
    userWalletGroups: any[];
    usingWalletGroupId: string[];
  };
};

export interface RebateInfo {
  inviteCode: string;
  inviteReferRate: string;
  inviteUrl: string;
  numberOfFriend: string;
  secondLevelInviteReferRate: string;
  totalRebateAmountInUsd: string;
  totalRebateVolumeInUsd: string;
  userId: string;
  userName: string;
}

export async function fetchUserInfo(): Promise<UserInfo> {
  const response = await api.get(`${requestUrl}/v1/account/account_info`);

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<UserInfo>>();

  return data.data!;
}

export function useUserInfo() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['userInfo'],
    queryFn: fetchUserInfo,
    enabled: !!session?.isLogin,
  });
}

/**
 * 获取返佣信息
 * @returns
 */
export async function fetchRebateInfo(): Promise<RebateInfo> {
  const response = await api.get(`${requestUrl}/v1/rebate/query_rebate_info`);
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<RebateInfo>>();
  return data.data!;
}

export function useRebateInfo() {
  return useQuery({
    queryKey: ['rebateInfo'],
    queryFn: fetchRebateInfo,
  });
}

export type HistoryTotalTokenValueProps = {
  currentTotalTokenValue: string;
  timeStamp: string;
};

export type TokenAssetsProps = {
  chainId: string;
  change24h: string;
  tokenAmount: string;
  tokenCa: string;
  tokenDecimals: string;
  tokenIcon: string;
  tokenName: string;
  tokenPrice: string;
  tokenSymbol: string;
  tokenValue: string;
  minWithdrawAmount: string;
  profitValue?: '';
  profitRange?: '';
};

export interface AccountFundResponse {
  historyTotalTokenValue: Array<HistoryTotalTokenValueProps>;
  pageNo: number;
  profitRange: string;
  profitValue: string;
  total: number;
  totalTokenValue: string;
  tokenAssets: Array<TokenAssetsProps>;
}

export type AccountHoldResponseItem = {
  buyTokenAmount: string;
  holdTokenAmount: string;
  sellTokenAmount: string;
  baseStandard: {
    buyTokenAverageFdv: string;
    buyTokenAveragePrice: string;
    buyTokenValue: string;
    holdTokenValue: string;
    realizedTokenProfit: string;
    sellTokenAveragePrice: string;
    sellTokenValue: string;
    totalTokenProfit: string;
    totalTokenProfitYield: string;
    unrealizedTokenProfit: string;
  };
  baseStandardToUsd: {
    buyTokenAverageFdv: string;
    buyTokenAveragePrice: string;
    buyTokenValue: string;
    holdTokenValue: string;
    realizedTokenProfit: string;
    sellTokenAveragePrice: string;
    sellTokenValue: string;
    totalTokenProfit: string;
    totalTokenProfitYield: string;
    unrealizedTokenProfit: string;
  };
  tokeInfo: {
    basePrice: string;
    chainId: string;
    change24h: string;
    tokenCa: string;
    tokenIcon: string;
    tokenName: string;
    tokenPrice: string;
    tokenPriceUsd: string;
    tokenSupply: string;
    tokenSymbol: string;
  };
};
export interface AccountHoldResponse {
  list: AccountHoldResponseItem[];
}

export type AccountHistoryResponseItem = {
  amount: string;
  cap: string;
  chainId: string;
  createTime: string;
  id: string;
  price: string;
  priceUsd: string;
  status: string;
  swapType: string;
  tokenCa: string;
  tokenIcon: string;
  tokenSymbol: string;
  tradeType: string;
  txHash: string;
  updateTime: string;
  value: string;
  valueUsd: string;
};

export interface AccountHistoryResponse {
  list: AccountHistoryResponseItem[];
}

export interface AccountFundProps {
  wallet_group_id: number;
  hide_small_tokens: boolean;
  page_no: number;
  page_size: number;
}

export async function fetchAccountFundList(
  params: AccountFundProps,
): Promise<AccountFundResponse> {
  const response = await api.get(`${requestUrl}/v1/account/fund_list`, {
    searchParams: { ...params },
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<AccountFundResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

/**
 * 获取账户资金列表的Hook
 * @param params 查询参数
 * @returns {UseQueryResult<AccountFundResponse>} 查询结果
 */
export function useFundList(
  params: AccountFundProps,
): UseQueryResult<AccountFundResponse, Error> {
  return useQuery<AccountFundResponse, Error>({
    queryKey: ['account-fund-list', params],
    queryFn: () => fetchAccountFundList(params),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 30 * 1000,
    retry: 2,
  });
}

export async function fetchMyHolding(
  chainId: number,
): Promise<AccountHoldResponse> {
  const response = await api.get(`${requestUrl}/v1/trade/query_my_holding`, {
    searchParams: {
      chain_id: chainId,
    },
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<AccountHoldResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export async function fetchMyTradeHistory(
  pageNo: number,
  pageSize: number,
): Promise<AccountHistoryResponse> {
  const response = await api.get(`${requestUrl}/v1/trade/query_trade_history`, {
    searchParams: {
      page_no: pageNo,
      page_size: pageSize,
    },
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<AccountHistoryResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export type OrderItem = {
  id: string;
  chainId: string;
  tokenIcon?: string;
  tokenCa: string;
  tokenSymbol: string;
  tradeType: string;
  swapType: string;
  cap: string;
  price: string;
  priceUsd: string;
  amount: string;
  value: string;
  valueUsd: string;
  status: string;
  createTime: string;
  updateTime: string;
};
export interface CurrentOrderResponse {
  total: number;
  list: OrderItem[];
}

export async function fetchCurrentOrder(
  pageNo: number,
  pageSize: number,
): Promise<CurrentOrderResponse> {
  const response = await api.get(`${requestUrl}/v1/trade/query_current_order`, {
    searchParams: {
      page_no: pageNo,
      page_size: pageSize,
    },
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<CurrentOrderResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }
  return data.data;
}

export interface UserTradeSettingRequest {
  /**
   * solgas类型, 只有1，2，3 三个档位对应普通，快速和超快
   */
  gas_type: number;
  chain_id: number;
  /**
   * 是否开启防夹, false不开启 true开启
   */
  is_anti_mev: boolean;
  /**
   * 是否设置自动滑点
   */
  is_auto_slippage: boolean;
  /**
   * 滑点pb, 5%就是500
   */
  slippage: string;
}

export async function updateUserTradeSetting(
  payload: UserTradeSettingRequest,
): Promise<void> {
  try {
    const response = await api.post(`${requestUrl}/v1/account/trade_setting`, {
      json: { ...payload },
    });
    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }
    const data = await response.json<ResponseMap<void>>();
    if (data.code !== SERVER_CODE.SUCCESS) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }
  } catch (error: any) {
    toaster.create({
      title: error.message,
      type: 'error',
    });
  }
}

export interface QuickBuyInfo {
  quickBuy1: string;
  quickBuy2: string;
  quickBuy3: string;
  quickBuy4: string;
  quickBuy5: string;
  quickBuy6: string;
}

export interface QuickSellInfo {
  quickSell1: string;
  quickSell2: string;
  quickSell3: string;
  quickSell4: string;
  quickSell5: string;
  quickSell6: string;
}

export interface QuickBuySellInfo {
  quickBuyList: [{ chainId: string; quickBuyInfo: QuickBuyInfo }];
  quickSellInfo: QuickSellInfo;
}

export async function requestTransactionConfig(
  chainId?: number,
): Promise<QuickBuySellInfo> {
  const response = await api.get(
    `${requestUrl}/v2/account/get_transaction_config`,
    {
      searchParams: chainId ? { chain_id: chainId } : undefined,
    },
  );
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<QuickBuySellInfo>>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }

  return data.data;
}

const defaultTransactionConfig: QuickBuySellInfo = {
  quickBuyList: [
    {
      chainId: '',
      quickBuyInfo: {
        quickBuy1: '0.1',
        quickBuy2: '0.2',
        quickBuy3: '0.5',
        quickBuy4: '1',
        quickBuy5: '2',
        quickBuy6: '5',
      },
    },
  ],
  quickSellInfo: {
    quickSell1: '25',
    quickSell2: '40',
    quickSell3: '50',
    quickSell4: '60',
    quickSell5: '80',
    quickSell6: '100',
  },
};

export function useTransactionConfig(chainId?: number) {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['transaction_config', chainId],
    queryFn: () =>
      requestTransactionConfig(chainId) ?? defaultTransactionConfig,
    initialData: defaultTransactionConfig,
    enabled: !!session?.isLogin,
  });
}

export interface QuickBuySellInfoPayload {
  chainId: number;
  quickBuyInfo: QuickBuyInfo;
  quickSellInfo: QuickSellInfo;
}

export async function updateQuickBuySellInfo(
  payload: QuickBuySellInfoPayload,
): Promise<void> {
  const response = await api.post(
    `${requestUrl}/v2/account/set_transaction_config`,
    {
      json: { ...payload },
    },
  );
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<void>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }
}
